import { create } from 'zustand';

const MAX_ENTRIES = 100;

function makeEntryId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `entry-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const useDevToolsStore = create((set, get) => ({
  isOpen: false,
  activeTab: 'console',
  panelHeight: 280,
  entries: [],
  activeEntryId: null,

  // Multi-terminal session state
  terminalSessions: [],
  activeTerminalSessionId: null,
  availableShells: [],
  selectedShellKey: null,

  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  open: (tab) =>
    set((s) => ({
      isOpen: true,
      activeTab: tab || s.activeTab,
    })),
  close: () => set({ isOpen: false }),
  setActiveTab: (tab) => set({ activeTab: tab, isOpen: true }),
  setPanelHeight: (height) =>
    set({ panelHeight: Math.max(150, Math.min(height, window.innerHeight * 0.6)) }),

  addTerminalSession: (label, shellKey) => {
    const id = makeEntryId();
    const session = { id, label, shellKey: shellKey || null, exited: false };
    set((s) => ({
      terminalSessions: [...s.terminalSessions, session],
      activeTerminalSessionId: id,
    }));
    return id;
  },

  removeTerminalSession: (id) =>
    set((s) => {
      const remaining = s.terminalSessions.filter((t) => t.id !== id);
      let nextActive = s.activeTerminalSessionId;
      if (nextActive === id) {
        const idx = s.terminalSessions.findIndex((t) => t.id === id);
        const fallback = remaining[Math.min(idx, remaining.length - 1)];
        nextActive = fallback ? fallback.id : null;
      }
      return { terminalSessions: remaining, activeTerminalSessionId: nextActive };
    }),

  markTerminalExited: (id) =>
    set((s) => ({
      terminalSessions: s.terminalSessions.map((t) =>
        t.id === id ? { ...t, exited: true } : t
      ),
    })),

  setActiveTerminalSession: (id) => set({ activeTerminalSessionId: id }),

  renameTerminalSession: (id, label) =>
    set((s) => ({
      terminalSessions: s.terminalSessions.map((t) =>
        t.id === id ? { ...t, label } : t
      ),
    })),

  setAvailableShells: (shells) => set({ availableShells: shells }),
  setSelectedShellKey: (key) => set({ selectedShellKey: key }),

  pushEntry: (entry) => {
    const id = makeEntryId();
    const full = {
      id,
      timestamp: Date.now(),
      method: entry.method || 'GET',
      url: entry.url || '',
      status: entry.status ?? null,
      statusText: entry.statusText || '',
      durationMs: entry.durationMs ?? null,
      requestHeaders: entry.requestHeaders || {},
      requestBody: entry.requestBody ?? null,
      responseHeaders: entry.responseHeaders || {},
      responseBody: entry.responseBody ?? null,
      responseSizeBytes: entry.responseSizeBytes ?? null,
      timeline: entry.timeline || null,
      error: entry.error ?? null,
    };
    set((s) => {
      const entries = [full, ...s.entries].slice(0, MAX_ENTRIES);
      return { entries, activeEntryId: id, isOpen: s.isOpen };
    });
    return id;
  },

  selectEntry: (id) => set({ activeEntryId: id }),
  clearEntries: () => set({ entries: [], activeEntryId: null }),

  getActiveEntry: () => {
    const { entries, activeEntryId } = get();
    return entries.find((e) => e.id === activeEntryId) || entries[0] || null;
  },
}));
